import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  Delete,
  Edit,
  OpenInNew,
  Pause,
  PlayArrow,
  Subtitles as TextOutputIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const TEXT_OUTPUT_URL =
  process.env.REACT_APP_DAB_OUTPUT_URL ||
  `${window.location.protocol}//${window.location.hostname}:${process.env.REACT_APP_DAB_OUTPUT_PORT || '8002'}`;

const emptyDabConfig = () => ({
  rotation_seconds: 10,
  station_name: '',
  delay_seconds: 0,
  items: [],
});

const emptyXperiConfig = () => ({
  max_tracks: 10,
  songs_only: true,
  delay_seconds: 0,
});

const DEFAULT_TEXT_NAME = 'Text';
const DEFAULT_XPERI_NAME = 'Xperi Playout';
const DEFAULT_PROVIDER_NAMES = new Set([DEFAULT_TEXT_NAME, 'DAB DLS', DEFAULT_XPERI_NAME]);

const providerLabel = (provider) => {
  if (provider === 'xperi_playout') return DEFAULT_XPERI_NAME;
  return DEFAULT_TEXT_NAME;
};

const defaultForm = () => ({
  id: null,
  name: '',
  provider: 'dab_dls',
  enabled: true,
  target_key: '',
  station_id: null,
  region_id: null,
  output_key: '',
  config: emptyDabConfig(),
});

export default function TextOutputsSettings() {
  const [outputs, setOutputs] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [newItemText, setNewItemText] = useState('');
  const [previews, setPreviews] = useState([]);
  const [filterTarget, setFilterTarget] = useState('all');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [outputsRes, targetsRes] = await Promise.all([
        axios.get(`${API_URL}/api/text-outputs`, { withCredentials: true }),
        axios.get(`${API_URL}/api/text-outputs/targets`, { withCredentials: true }),
      ]);
      setOutputs(outputsRes.data || []);
      setTargets(targetsRes.data || []);
    } catch (e) {
      console.error('Error loading text outputs:', e);
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to load text outputs' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredOutputs = useMemo(() => {
    if (filterTarget === 'all') return outputs;
    return outputs.filter((o) => {
      if (filterTarget.startsWith('station-')) {
        const stationId = Number(filterTarget.replace('station-', ''));
        return o.station_id === stationId && !o.region_id;
      }
      if (filterTarget.startsWith('region-')) {
        const regionId = Number(filterTarget.replace('region-', ''));
        return o.region_id === regionId;
      }
      return true;
    });
  }, [outputs, filterTarget]);

  const openCreate = (provider = 'dab_dls') => {
    const firstTarget = targets[0];
    setForm({
      ...defaultForm(),
      provider,
      name: provider === 'xperi_playout' ? DEFAULT_XPERI_NAME : DEFAULT_TEXT_NAME,
      target_key: firstTarget?.target_key || '',
      station_id: firstTarget?.station_id || null,
      region_id: firstTarget?.region_id || null,
      config: provider === 'xperi_playout' ? emptyXperiConfig() : emptyDabConfig(),
    });
    setNewItemText('');
    setPreviews([]);
    setDialogOpen(true);
  };

  const openEdit = async (output) => {
    const targetKey = output.region_id
      ? `region-${output.region_id}`
      : `station-${output.station_id}`;
    setForm({
      id: output.id,
      name: output.name || '',
      provider: output.provider || 'dab_dls',
      enabled: output.enabled === true,
      target_key: targetKey,
      station_id: output.station_id,
      region_id: output.region_id,
      output_key: output.output_key || '',
      config:
        output.provider === 'xperi_playout'
          ? { ...emptyXperiConfig(), ...(output.config || {}) }
          : { ...emptyDabConfig(), ...(output.config || {}) },
    });
    setNewItemText('');
    setPreviews([]);
    setDialogOpen(true);
    if (output.provider === 'dab_dls') {
      try {
        const res = await axios.get(`${API_URL}/api/text-outputs/${output.id}/preview`, {
          withCredentials: true,
        });
        setPreviews(res.data?.items || []);
      } catch {
        setPreviews([]);
      }
    }
  };

  const applyTarget = (targetKey) => {
    const target = targets.find((t) => t.target_key === targetKey);
    setForm((prev) => ({
      ...prev,
      target_key: targetKey,
      station_id: target?.station_id || null,
      region_id: target?.region_id || null,
    }));
  };

  const setProvider = (provider) => {
    setForm((prev) => ({
      ...prev,
      provider,
      name:
        prev.name && !DEFAULT_PROVIDER_NAMES.has(prev.name)
          ? prev.name
          : provider === 'xperi_playout'
            ? DEFAULT_XPERI_NAME
            : DEFAULT_TEXT_NAME,
      config: provider === 'xperi_playout' ? emptyXperiConfig() : emptyDabConfig(),
    }));
    setPreviews([]);
  };

  const handleSave = async () => {
    if (!form.target_key) {
      setMessage({ type: 'error', text: 'Select a station or region' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        name: form.name,
        provider: form.provider,
        enabled: form.enabled,
        station_id: form.region_id ? undefined : form.station_id,
        region_id: form.region_id || null,
        output_key: form.output_key || undefined,
        config: form.config,
      };

      if (form.id) {
        await axios.put(`${API_URL}/api/text-outputs/${form.id}`, payload, { withCredentials: true });
        setMessage({ type: 'success', text: 'Text output saved' });
      } else {
        await axios.post(`${API_URL}/api/text-outputs`, payload, { withCredentials: true });
        setMessage({ type: 'success', text: 'Text output created' });
      }
      setDialogOpen(false);
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save text output' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (output) => {
    if (!window.confirm(`Delete "${output.name}" (${providerLabel(output.provider)})?`)) return;
    try {
      await axios.delete(`${API_URL}/api/text-outputs/${output.id}`, { withCredentials: true });
      setMessage({ type: 'success', text: 'Text output deleted' });
      await loadAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to delete text output' });
    }
  };

  const feedUrl = (output) => {
    if (!output) return '';
    if (output.provider === 'xperi_playout') {
      return `${TEXT_OUTPUT_URL}/api/text-outputs/xperi/${output.output_key}`;
    }
    return `${TEXT_OUTPUT_URL}/api/text-outputs/dab/${output.output_key}`;
  };

  const copyText = async (text, label = 'URL') => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: `${label} copied to clipboard` });
    } catch {
      setMessage({ type: 'error', text: `Failed to copy ${label}` });
    }
  };

  // ---- DAB item helpers ----
  const items = form.config?.items || [];

  const addItem = (text) => {
    const value = (text ?? newItemText).trim();
    if (!value) return;
    setForm((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        items: [...(prev.config.items || []), { text: value, enabled: true }],
      },
    }));
    setNewItemText('');
  };

  const updateItemText = (index, text) => {
    const next = [...items];
    next[index] = { ...next[index], text };
    setForm((prev) => ({ ...prev, config: { ...prev.config, items: next } }));
  };

  const toggleItem = (index) => {
    const next = [...items];
    next[index] = { ...next[index], enabled: next[index].enabled === false };
    setForm((prev) => ({ ...prev, config: { ...prev.config, items: next } }));
  };

  const removeItem = (index) => {
    const next = [...items];
    next.splice(index, 1);
    setForm((prev) => ({ ...prev, config: { ...prev.config, items: next } }));
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const next = [...items];
    const tmp = next[index];
    next[index] = next[newIndex];
    next[newIndex] = tmp;
    setForm((prev) => ({ ...prev, config: { ...prev.config, items: next } }));
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
        Text Output
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure multiple text / metadata outputs per station or split region.
        Use <strong>Text</strong> for rotating display text (DAB DLS, HD Radio Text, FM text, and similar),
        or <strong>Xperi Playout</strong> for the JSON now-playing feed used by Xperi / AIM apps.
      </Typography>

      {message.text && (
        <Alert severity={message.type || 'info'} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openCreate('dab_dls')}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                Add Text
              </Button>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => openCreate('xperi_playout')}
                sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
              >
                Add Xperi Playout
              </Button>
            </Box>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterTarget}
                label="Filter"
                onChange={(e) => setFilterTarget(e.target.value)}
              >
                <MenuItem value="all">All stations & regions</MenuItem>
                {targets.map((t) => (
                  <MenuItem key={t.target_key} value={t.target_key}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'transparent', border: '1px solid var(--rs-border)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#22d3ee' }}>Name</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Station / Region</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Type</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Status</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Feed</TableCell>
                  <TableCell sx={{ color: '#22d3ee', width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOutputs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      {loading
                        ? 'Loading…'
                        : 'No text outputs yet. Add a Text or Xperi Playout output for any station or region.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOutputs.map((output) => (
                    <TableRow key={output.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {output.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {output.output_key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {output.region_name
                            ? `${output.station_name || 'Station'} / ${output.region_name}`
                            : output.station_name || `Station ${output.station_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={providerLabel(output.provider)}
                          sx={{
                            bgcolor: output.provider === 'xperi_playout' ? '#f59e0b22' : '#22d3ee22',
                            color: output.provider === 'xperi_playout' ? '#f59e0b' : '#22d3ee',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={
                            output.enabled && (output.is_running || output.provider === 'xperi_playout')
                              ? <PlayArrow sx={{ fontSize: '14px !important' }} />
                              : <Pause sx={{ fontSize: '14px !important' }} />
                          }
                          label={
                            !output.enabled
                              ? 'Off'
                              : output.provider === 'xperi_playout'
                                ? 'Enabled'
                                : output.is_running
                                  ? 'Running'
                                  : 'Enabled'
                          }
                          color={
                            output.enabled && (output.is_running || output.provider === 'xperi_playout')
                              ? 'success'
                              : output.enabled
                                ? 'warning'
                                : 'default'
                          }
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        {output.provider === 'dab_dls' && output.current_text && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              fontFamily: 'monospace',
                              color: '#4ade80',
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {output.current_text}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {feedUrl(output)}
                          </Typography>
                          <IconButton size="small" onClick={() => copyText(feedUrl(output))} title="Copy URL">
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => window.open(feedUrl(output), '_blank')} title="Open">
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" sx={{ color: '#22d3ee' }} onClick={() => openEdit(output)} title="Edit">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(output)} title="Delete">
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee', pb: form.provider === 'dab_dls' ? 0.5 : undefined }}>
          {form.id ? 'Edit Text Output' : 'Add Text Output'}
          {form.provider === 'dab_dls' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
              Suitable for DAB DLS, HD Radio Text, FM text, and similar rotating text displays.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.provider}
                  label="Type"
                  onChange={(e) => setProvider(e.target.value)}
                  disabled={Boolean(form.id)}
                >
                  <MenuItem value="dab_dls">Text</MenuItem>
                  <MenuItem value="xperi_playout">Xperi Playout</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Station / Region</InputLabel>
                <Select
                  value={form.target_key}
                  label="Station / Region"
                  onChange={(e) => applyTarget(e.target.value)}
                >
                  {targets.map((t) => (
                    <MenuItem key={t.target_key} value={t.target_key}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Output key (URL slug)"
                value={form.output_key}
                onChange={(e) => setForm((prev) => ({ ...prev, output_key: e.target.value }))}
                helperText="Leave blank to auto-generate from mountpoint"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Enabled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Now Playing Delay (seconds)"
                value={form.config.delay_seconds ?? 0}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    config: {
                      ...prev.config,
                      delay_seconds: Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)),
                    },
                  }))
                }
                inputProps={{ min: 0, max: 60 }}
                helperText="Delay metadata up to 60s when the stream is behind"
              />
            </Grid>

            {form.provider === 'dab_dls' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Station Name (for {station_name})"
                    value={form.config.station_name || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        config: { ...prev.config, station_name: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Rotation Interval (seconds)"
                    value={form.config.rotation_seconds || 10}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        config: {
                          ...prev.config,
                          rotation_seconds: Math.max(1, parseInt(e.target.value, 10) || 10),
                        },
                      }))
                    }
                    inputProps={{ min: 1, max: 300 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Text Items
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="New text item"
                      placeholder="e.g. Now Playing: {artist} - {title}"
                      value={newItemText}
                      onChange={(e) => setNewItemText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addItem();
                      }}
                      inputProps={{ maxLength: 128 }}
                      helperText={`${newItemText.length}/128 characters (common max for DAB DLS / HD Radio / FM)`}
                    />
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => addItem()}
                      disabled={!newItemText.trim()}
                      sx={{ minWidth: 100, bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1, lineHeight: '24px' }}>
                      Quick add:
                    </Typography>
                    {[
                      { label: 'Now Playing', text: 'Now Playing: {artist} - {title}' },
                      { label: 'Artist - Title', text: '{artist} - {title}' },
                      { label: 'Next Music', text: 'Up Next: {next_artist} - {next_title}' },
                      { label: 'Station Name', text: '{station_name}' },
                      { label: 'Date & Time', text: '{day}, {date} - {time}' },
                    ].map((tpl) => (
                      <Chip
                        key={tpl.label}
                        label={tpl.label}
                        size="small"
                        variant="outlined"
                        onClick={() => addItem(tpl.text)}
                        sx={{ cursor: 'pointer', borderColor: '#3a4456', '&:hover': { borderColor: '#22d3ee', color: '#22d3ee' } }}
                      />
                    ))}
                  </Box>

                  {items.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                      <TextOutputIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                      <Typography variant="body2">No text items yet.</Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ bgcolor: 'transparent', border: '1px solid var(--rs-border)' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40, color: '#999' }}>#</TableCell>
                            <TableCell sx={{ color: '#999' }}>Text Template</TableCell>
                            <TableCell sx={{ width: 70, color: '#999', textAlign: 'center' }}>Status</TableCell>
                            <TableCell sx={{ width: 140, color: '#999', textAlign: 'center' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item, idx) => (
                            <TableRow key={idx} sx={{ opacity: item.enabled === false ? 0.5 : 1 }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: '#22d3ee', fontWeight: 600 }}>
                                  {idx + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={item.text || ''}
                                  onChange={(e) => updateItemText(idx, e.target.value)}
                                  inputProps={{ maxLength: 128 }}
                                  variant="standard"
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={item.enabled !== false ? 'On' : 'Off'}
                                  size="small"
                                  color={item.enabled !== false ? 'success' : 'default'}
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <IconButton size="small" onClick={() => toggleItem(idx)}>
                                  {item.enabled !== false ? <Visibility sx={{ fontSize: 18 }} /> : <VisibilityOff sx={{ fontSize: 18 }} />}
                                </IconButton>
                                <IconButton size="small" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                                  <ArrowUpward sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}>
                                  <ArrowDownward sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => removeItem(idx)} sx={{ color: '#ef4444' }}>
                                  <Delete sx={{ fontSize: 18 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                {previews.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Live Preview
                    </Typography>
                    {previews.map((preview, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          p: 1,
                          bgcolor: preview.enabled ? 'var(--rs-bg)' : 'var(--rs-paper)',
                          borderRadius: 1,
                          border: preview.enabled ? '1px solid #22d3ee33' : '1px solid var(--rs-border)',
                          opacity: preview.enabled ? 1 : 0.5,
                        }}
                      >
                        <Chip label={idx + 1} size="small" sx={{ minWidth: 28, bgcolor: '#22d3ee22', color: '#22d3ee' }} />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: preview.enabled ? '#4ade80' : '#999' }}>
                          {preview.preview || '(empty)'}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Placeholders: {'{artist}'}, {'{title}'}, {'{album}'}, {'{next_artist}'}, {'{next_title}'}, {'{next_album}'}, {'{station_name}'}, {'{date}'}, {'{time}'}, {'{day}'}
                    {' '}— next_* only resolves to the next track in a music category.
                  </Typography>
                </Grid>
              </>
            )}

            {form.provider === 'xperi_playout' && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Serves an HTTPS-friendly JSON feed in the Xperi / AIM Playout format
                    (<code>nowPlaying</code> array with playing / history / queued tracks).
                    Queued items use the next music-category tracks. Point the app or CDN at the feed URL after saving.
                  </Alert>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max tracks in feed"
                    value={form.config.max_tracks || 10}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        config: {
                          ...prev.config,
                          max_tracks: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 10)),
                        },
                      }))
                    }
                    inputProps={{ min: 1, max: 50 }}
                    helperText="Recommended maximum is 10"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.config.songs_only !== false}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            config: { ...prev.config, songs_only: e.target.checked },
                          }))
                        }
                      />
                    }
                    label="Music categories only (exclude voicetracks / ads)"
                  />
                </Grid>
              </>
            )}

            {form.output_key && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Public feed URL
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {form.provider === 'xperi_playout'
                    ? 'Point the Xperi / AIM app at this HTTPS URL for the playout JSON feed.'
                    : 'Point your DAB multiplexer, HD Radio encoder, FM RDS encoder, or similar at this URL to read the rotating text.'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'var(--rs-bg)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid var(--rs-border)',
                      wordBreak: 'break-all',
                      flex: 1,
                    }}
                  >
                    {feedUrl(form)}
                  </Typography>
                  <IconButton size="small" onClick={() => copyText(feedUrl(form))} title="Copy URL">
                    <ContentCopy sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => window.open(feedUrl(form), '_blank')} title="Open">
                    <OpenInNew sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.target_key}
            sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
